/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';

import CardData, { CardDataItem } from 'components/v2/CardData';
import { newLayout } from 'theme';

/**
 * CardDataItemList
 *
 * A flexible component to render an array of CardDataItems or custom React elements
 * in either single-column or multi-column layout.
 *
 * Features:
 * - Supports multiple columns per row with consistent spacing.
 * - Labels are right-aligned, values are left-aligned.
 * - Handles custom icons, label styles, and inline React elements.
 * - Automatically wraps plain `value` in `LeftContentText`.
 * - Custom React elements (like dividers) span the full width of the row.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array<Object|React.ReactElement>} props.items - Array of items to display. Each item can be a data object or a custom React element.
 * @param {number} [props.numberOfColumns=1] - Number of columns per row for standard items.
 * @param {string} [props.columnGap=newLayout.spacing.s4] - Gap between columns.
 * @param {string} [props.rowGap=newLayout.spacing.s2] - Gap between rows.
 * @param {boolean} [props.inline=false] - Whether to render items inline.
 * @returns {JSX.Element|null} The rendered card data item list, or null if no items.
 */

/**
 * CardDataItemList
 *
 * A flexible component to render an array of CardDataItems or custom React elements
 * in either single-column or multi-column layout.
 *
 * Features:
 * - Supports multiple columns per row with consistent spacing.
 * - Labels are left-aligned, values are right-aligned.
 * - Handles custom icons, formatted labels, and inline React elements.
 * - Automatically wraps plain `value` in `LeftContentText`.
 * - Custom React elements (like dividers) span the full width of the row.
 *
 * @param {Object} props
 * @param {Array} props.items - List of items or React elements to render. Each item can be:
 *   - {ReactNode} [content] - Full control over rendering; overrides `value`.
 *   - {string|number} [value] - Displayed in `LeftContentText` if `content` is not provided.
 *   - {string} [label] - Label text displayed on the left.
 *   - {ReactNode} [formattedLabel] - Optional right-aligned content.
 *   - {ReactNode} [icon] - Optional icon element.
 *   - {string} [iconColor] - Color for the icon.
 *   - {string} [iconStrokeColor] - Stroke color for the icon.
 *   Or a valid React element (e.g., `<StyledCardHorizontalLine />`) which will span full width.
 *
 * @param {number} [props.columns=1] - Maximum number of items per row.
 * @param {string} [props.columnGap=newLayout.spacing.s4] - Horizontal gap between columns.
 * @param {string} [props.rowGap=newLayout.spacing.s2] - Vertical gap between rows.
 * @param {string} [props.labelColor] - Label text color.
 * @param {string} [props.labelFontSize] - Font size for labels.
 * @param {string} [props.labelFontWeight] - Font weight for labels.
 * @param {string} [props.labelLineHeight] - Line height for labels.
 * @param {boolean} [props.inline=true] - Whether items in a row should use inline layout.
 */

const CardDataItemList = ({
  items,
  numberOfColumns = 1,
  columnGap = newLayout.spacing.s4,
  rowGap = newLayout.spacing.s2,
  inline = false,
  ...props
}) => {
  const rows = useMemo(() => {
    if (!items?.length) return [];

    let tempRow = [];
    const cardRows = [];

    // Iterate over items and organize them into rows
    items.forEach((item) => {
      // Skip completely empty items
      if (
        !item ||
        (!item.value && !item.content && !React.isValidElement(item))
      )
        return;

      const isCustomElement = React.isValidElement(item);

      if (isCustomElement) {
        // Custom elements span full row
        if (tempRow.length) {
          // then push the till created tempRow and clear it
          cardRows.push([...tempRow]);
          tempRow = [];
        }
        cardRows.push([item]);
      } else {
        // Add the current item to the temporary row
        tempRow.push(item);
        // Once the temporary row reaches the specified column count,
        // push it to the cardRows array and reset the temporary row
        if (tempRow.length === numberOfColumns) {
          cardRows.push([...tempRow]);
          tempRow = [];
        }
      }
    });

    if (tempRow.length) cardRows.push([...tempRow]);

    return cardRows;
  }, [items]);

  if (!items?.length) return null;

  return (
    <CardData>
      {rows.map((rowItems, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            display: 'flex',
            gap: columnGap,
            width: '100%',
            marginBottom: rowIndex < rows.length - 1 ? rowGap : 0,
          }}
        >
          {rowItems.map((item, colIndex) => {
            const isCustomElement =
              React.isValidElement(item) && item.type !== CardDataItem;

            if (isCustomElement)
              return <React.Fragment key={colIndex}>{item}</React.Fragment>;

            return (
              <CardDataItem
                key={`${rowIndex}-${colIndex}`}
                content={item.content}
                icon={item.icon}
                iconColor={item.iconColor}
                iconStrokeColor={item.iconStrokeColor}
                label={item?.label}
                labelStyles={item?.labelStyles}
                inline={inline}
                isMultiline={item.isMultiline}
                isPreview={item.isPreview}
                previewLines={item.previewLines}
                style={{ flex: 1, minWidth: 0 }}
                {...props}
              />
            );
          })}
        </div>
      ))}
    </CardData>
  );
};

CardDataItemList.propTypes = {
  items: PropTypes.array,
  numberOfColumns: PropTypes.number,
  columnGap: PropTypes.string,
  rowGap: PropTypes.string,
  inline: PropTypes.bool,
};

export default memo(CardDataItemList);
